import { useCallback, useEffect, useRef, useState } from 'react';
import { parseUnits } from 'viem';

import { toast } from '@/components/ui/toast';
import { runWithFundAuthorization } from '@/features/fundAuthorization';
import {
    ANCHOR_CHAINS,
    useAuthedApi,
    useFactionsAuthedApi,
    useSpeedBalance,
    useWallet,
} from '@/features/wallet';
import {
    toAnchorChainId,
    type AnchorChainId,
} from '@/features/wallet/config/anchors';
import { wolfswapFactionsApi } from '@/lib/api';
import {
    fetchSpeedJob,
    fetchWithdrawQuote,
    registerBridgeIntent,
    startWithdraw,
    USDC_DECIMALS,
} from '@/lib/api/speed';
import type {
    BridgeName,
    BridgeQuoteResponse,
    SpeedJobResponse,
} from '@/lib/api/speed';
import {
    describeErrorForUser,
    isUserRejection,
    userErrorText,
} from '@/lib/errors';

import { bridgeRequestField } from '../bridgeOverride';
import {
    bridgeFeesFromJob,
    normalizeTransferFees,
} from '../services/bridgeFees';
import {
    assertBridgeIntentMatchesIntent,
    bridgeIntentFromJob,
    sendBridgeIntentRegistration,
} from '../services/bridgeIntent';
import { resolveHyperUsdcAddress } from '../services/directDeposit';
import {
    reportJobException,
    setActiveJobForErrorReports,
} from '../services/jobErrorReporter';
import type { CustodialWithdrawState, CustodialWithdrawStatus } from '../types';

const TOAST_DURATION = 10_000;

/** Poll the backend job status every 3s for up to 10 minutes. */
const STATUS_POLL_INTERVAL_MS = 3_000;
const STATUS_POLL_TIMEOUT_MS = 10 * 60_000;

const INITIAL_STATE: CustodialWithdrawState = {
    status: 'idle',
    amount: null,
    error: null,
    transferId: null,
    jobId: null,
    txHash: null,
    progress: null,
    progressMessage: null,
    progressStep: null,
    bridge: null,
    bridgeFeeUsd: null,
};

function logStep(step: string, data?: unknown) {
    console.log(`[CustodialWithdraw] ${step}`, data ?? '');
}

const sleep = async (ms: number) =>
    new Promise<void>((resolve) => setTimeout(resolve, ms));

function transferIdFromJob(job: SpeedJobResponse): number | null {
    return job.transferId ?? job.response?.transferId ?? null;
}

function txHashFromJob(job: SpeedJobResponse): string | null {
    const response = job.response ?? {};
    return (
        response.txHashes?.at(-1) ?? response.coreWriterSendAssetTxHash ?? null
    );
}

function amountFromJob(job: SpeedJobResponse): bigint | null {
    const raw = job.response?.amount;
    if (typeof raw !== 'string' || !/^\d+$/.test(raw)) {
        return null;
    }
    return BigInt(raw);
}

function statusFromJob(job: SpeedJobResponse): CustodialWithdrawStatus {
    if (job.status === 'settled') {
        return 'success';
    }
    if (job.status === 'failed' || job.status === 'refunded') {
        return 'failed';
    }
    if (job.status === 'queued' || job.status === 'running') {
        return 'submitting';
    }
    return 'bridging';
}

/** The parked-for-re-signature shape: the hop was re-routed and needs a new intent. */
function isAwaitingBridgeIntent(job: SpeedJobResponse): boolean {
    return (
        job.status === 'actionRequired' && job.step === 'awaitingBridgeIntent'
    );
}

/**
 * Copy for a parked withdrawal whose replacement route is carried by a
 * DIFFERENT bridge than the one the user last saw, or null when nothing
 * changed.
 *
 * This is the one moment a user can be asked to sign for a price they never
 * agreed to: the first quote said Relay and cents, the re-quote says deBridge
 * and dollars, and the only visible difference would otherwise be a number in
 * a hint line they have already read past. So it becomes the prompt itself.
 */
function bridgeRouteChangeMessage(
    job: SpeedJobResponse,
    seen: CustodialWithdrawState,
    fees: { bridge: BridgeName | null; bridgeFeeUsd: number | null },
): string | null {
    if (!isAwaitingBridgeIntent(job)) return null;
    if (seen.jobId !== job.jobId) return null;
    // nothing to compare against, or the same bridge as before: the ordinary
    // action-required message stands.
    if (!seen.bridge || !fees.bridge || fees.bridge === seen.bridge) {
        return null;
    }
    const fee = fees.bridgeFeeUsd ?? seen.bridgeFeeUsd ?? null;
    return fee === null
        ? 'The bridge route changed. Please sign to continue.'
        : `The bridge route changed and the fee is now $${fee.toFixed(2)}. ` +
              'Please sign to continue.';
}

function failureFromJob(job: SpeedJobResponse): Error {
    // a refunded job carries the bridge's own account when the backend has it (why it refunded and
    // where the money is now); the bare sentence is the fallback, never the override
    return new Error(
        job.error ||
            (job.status === 'refunded'
                ? 'Withdrawal was refunded. Your funds are back in your trading wallet on HyperEVM.'
                : 'Withdrawal failed.'),
    );
}

interface UseCustodialWithdrawParams {
    /** Called after the withdraw saga settles successfully. */
    onSuccess?: () => void;
}

interface UseCustodialWithdrawResult {
    state: CustodialWithdrawState;
    status: CustodialWithdrawStatus;
    /** Withdraw a specific amount (USDC base units, 6dp) to the user's EOA on that chain. */
    withdraw: (
        amount: bigint | undefined,
        destinationChainId: AnchorChainId,
    ) => Promise<void>;
    /** Withdraw the full available speed balance to the user's EOA on that chain. */
    withdrawAll: (destinationChainId: AnchorChainId) => Promise<void>;
    /** Reset state back to idle. */
    reset: () => void;
    /**
     * Display pre-quote for an amount the user is still typing: what the bridge
     * would cost and what would land. Read-only, reserves nothing, and never
     * carried into execution (prepare re-quotes). Feeds the form's fee line and
     * the gate on its button.
     */
    quoteWithdrawal: (
        amount: bigint,
        destinationChainId: AnchorChainId,
        signal: AbortSignal,
        destinationCurrency?: string,
    ) => Promise<BridgeQuoteResponse>;
    /** Attach UI state to an existing backend withdraw job after a refresh. */
    attach: (job: SpeedJobResponse) => Promise<void>;
    /**
     * Fold a job we learned about from the jobs list into this state, terminal states
     * included. The poll loop is the normal path, but it only runs in the tab that started
     * or attached to the job and it stops at its own deadline, so a job that goes terminal
     * outside that window needs this backstop.
     */
    syncFromJob: (job: SpeedJobResponse) => void;
    isWithdrawing: boolean;
}

/**
 * State machine for custodial ("Quick Trade") withdrawals via the NEW Relay
 * saga. Funds return to the user's connected MetaMask EOA on the origin chain
 * (Cronos USDC).
 *
 * The backend drives the saga, but the user's own wallet has to authorize the
 * bridge hop first: a withdrawal signs ONE Cronos transaction
 * (`ServiceWalletRegistry.registerBridgeIntent`, see
 * `services/bridgeIntent.ts`) that pins the hop's request id, token, amount and
 * recipient, plus the usual EIP-712 fund proof. That registration normally
 * happens inside the fund-authorization ceremony, before the job even exists;
 * if the hop later has to be re-routed, the job parks at `actionRequired` /
 * `awaitingBridgeIntent` with a fresh descriptor and the poll loop below asks
 * for one more registration.
 *
 * States: idle → submitting → bridging → success / failed
 *
 * On success, calls `onSuccess` (typically a balance refetch).
 */
export function useCustodialWithdrawManager({
    onSuccess,
}: UseCustodialWithdrawParams = {}): UseCustodialWithdrawResult {
    const [state, setState] = useState<CustodialWithdrawState>(INITIAL_STATE);
    const lockRef = useRef(false);
    const { address } = useWallet();
    const authedApi = useAuthedApi();
    // job status reads moved to factions.backend's speed facet (read-only);
    // startWithdraw (sensitive) stays on MoonHub.API.
    const factionsApi = useFactionsAuthedApi();
    const { numericWithdrawable } = useSpeedBalance();

    const reset = useCallback(() => {
        setState(INITIAL_STATE);
    }, []);

    // read the live state from callbacks without making them change identity every time it
    // moves (they are wired into effects that would otherwise re-run on every update).
    const stateRef = useRef(state);
    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    // Bridge intents this tab has already registered on chain, by id. The job
    // stays at `awaitingBridgeIntent` for a poll or two after the transaction
    // lands, and re-prompting for an id the user already paid gas for would be
    // both confusing and a guaranteed revert (the registry is write-once).
    const registeredBridgeIntentIds = useRef<Set<string>>(new Set());

    // USDC on HyperEVM, read from the backend rather than hardcoded, and only
    // once per mount: it is the token a bridge intent must name.
    const hyperUsdcRef = useRef<string | null>(null);
    const resolveHyperUsdc = useCallback(async (): Promise<string | null> => {
        if (hyperUsdcRef.current) return hyperUsdcRef.current;
        try {
            hyperUsdcRef.current = await resolveHyperUsdcAddress(
                factionsApi,
                wolfswapFactionsApi,
            );
        } catch (error) {
            logStep('Could not resolve the HyperEVM USDC address', error);
            return null;
        }
        return hyperUsdcRef.current;
    }, [factionsApi]);

    const trackWithdrawJob = useCallback(
        async (
            initialJob: SpeedJobResponse,
            amount: bigint | null,
            // null when attaching to a job another tab (or session) started:
            // the chain is then read off the descriptor itself, whose pinned
            // registry/destination checks still have to agree.
            destinationChainId: AnchorChainId | null,
        ) => {
            if (!authedApi || !factionsApi) {
                throw new Error('Connect & sign in first');
            }
            let deadline = Date.now() + STATUS_POLL_TIMEOUT_MS;
            let job = initialJob;

            for (;;) {
                // something else already settled this job (syncFromJob folding in a terminal
                // status from the jobs list). Stop, rather than re-painting the progress state
                // over a finished withdrawal on the next tick.
                const observed = stateRef.current;
                if (
                    observed.jobId === job.jobId &&
                    (observed.status === 'failed' ||
                        observed.status === 'success')
                ) {
                    return;
                }
                const txHash = txHashFromJob(job);
                // the route's own fee, once the backend has quoted one. Kept
                // sticky: a later poll that omits `fees` must not drop the
                // figure the form is already showing.
                const fees = bridgeFeesFromJob(job);
                // A parked withdrawal gets RE-QUOTED, and the replacement route
                // can be a different bridge than the one the user was shown and
                // agreed to. They are about to sign for it, so a changed price
                // has to be the thing the prompt says, not the standard
                // "confirm in your wallet" line. `observed` is the last state
                // the user actually saw rendered, which is exactly the
                // comparison this needs.
                const routeChangeMessage = bridgeRouteChangeMessage(
                    job,
                    observed,
                    fees,
                );
                setState((prev) => ({
                    ...prev,
                    status: statusFromJob(job),
                    amount: amount ?? prev.amount,
                    jobId: job.jobId,
                    transferId: transferIdFromJob(job),
                    progress: job.progress,
                    progressMessage: routeChangeMessage ?? job.message,
                    progressStep: job.step,
                    txHash: txHash ?? prev.txHash,
                    bridge: fees.bridge ?? prev.bridge ?? null,
                    bridgeFeeUsd:
                        fees.bridgeFeeUsd ?? prev.bridgeFeeUsd ?? null,
                }));
                logStep('Withdraw job status', {
                    jobId: job.jobId,
                    status: job.status,
                    step: job.step,
                });

                if (job.status === 'settled') {
                    setState((prev) => ({
                        ...prev,
                        status: 'success',
                        amount: amount ?? prev.amount,
                        txHash: txHash ?? prev.txHash,
                        progress: 100,
                        progressMessage: 'Withdrawal confirmed',
                    }));
                    toast.success('Funds withdrawn successfully.', {
                        duration: TOAST_DURATION,
                    });
                    onSuccess?.();
                    return;
                }

                if (job.status === 'refunded' || job.status === 'failed') {
                    throw failureFromJob(job);
                }

                // The hop had to be re-routed, so the intent the user
                // registered up front no longer describes it. Nothing moves
                // until their wallet registers the new one.
                if (
                    job.status === 'actionRequired' &&
                    job.step === 'awaitingBridgeIntent'
                ) {
                    const descriptor = bridgeIntentFromJob(job);
                    if (!descriptor) {
                        throw new Error(
                            'This withdrawal needs one more wallet ' +
                                'confirmation, but the details did not ' +
                                'arrive. Please try again in a moment.',
                        );
                    }
                    const key = descriptor.id.toLowerCase();
                    if (!registeredBridgeIntentIds.current.has(key)) {
                        const registryAnchor =
                            destinationChainId ??
                            toAnchorChainId(descriptor.registryChainId);
                        assertBridgeIntentMatchesIntent(descriptor, {
                            connectedAddress: address,
                            amount: amount ?? amountFromJob(job),
                            hyperUsdcAddress: await resolveHyperUsdc(),
                            anchorChainId: registryAnchor,
                        });
                        setState((prev) => ({
                            ...prev,
                            progress: job.progress,
                            progressStep: job.step,
                            progressMessage:
                                routeChangeMessage ??
                                'Confirm the withdrawal in your wallet',
                        }));
                        const registrationHash =
                            await sendBridgeIntentRegistration(
                                descriptor,
                                registryAnchor,
                            );
                        registeredBridgeIntentIds.current.add(key);
                        logStep('Bridge intent registered', {
                            jobId: job.jobId,
                            id: descriptor.id,
                            txHash: registrationHash,
                        });
                        const resumed = await registerBridgeIntent(
                            authedApi,
                            job.jobId,
                            { id: descriptor.id, txHash: registrationHash },
                        );
                        // eslint-disable-next-line require-atomic-updates -- one sequential poll loop; nothing else writes `job`
                        job = resumed;
                        // the prompt can stay open for as long as the user
                        // likes, so the bridge gets its full window from the
                        // moment they confirmed rather than from before.
                        deadline = Date.now() + STATUS_POLL_TIMEOUT_MS;
                        // the loop head repaints from the resumed job
                        continue;
                    }
                }

                if (Date.now() >= deadline) {
                    throw new Error(
                        'Withdrawal is taking longer than expected. ' +
                            'It may still complete, check your wallet shortly.',
                    );
                }

                await sleep(STATUS_POLL_INTERVAL_MS);
                const polled = await fetchSpeedJob(
                    factionsApi,
                    initialJob.jobId,
                );
                // eslint-disable-next-line require-atomic-updates -- one sequential poll loop; nothing else writes `job`
                job = polled;
            }
        },
        [address, authedApi, factionsApi, onSuccess, resolveHyperUsdc],
    );

    const failWithdraw = useCallback(
        (error: unknown) => {
            const described = describeErrorForUser(error, {
                operation: 'Withdrawal',
            });
            const message = userErrorText(described);

            // Declining the wallet prompt is a choice, not a fault: say so plainly
            // and skip the error toast, the same way the deposit flow does.
            if (isUserRejection(error) || isUserRejection(message)) {
                logStep('User rejected');
                onSuccess?.();
                setState((prev) => ({
                    ...prev,
                    status: 'failed',
                    error: 'Transaction cancelled.',
                }));
                return;
            }

            console.error('[CustodialWithdraw] failed:', error);
            // re-read balances on failure too: a withdrawal can fail after funds have already left the
            // custodial wallet, so the displayed figures are stale exactly when they matter most.
            onSuccess?.();
            setState((prev) => ({
                ...prev,
                status: 'failed',
                error: message,
            }));
            toast.error(described.title, {
                description: message,
                duration: TOAST_DURATION,
            });
        },
        [onSuccess],
    );

    // where the payout lands AND which chain's registry records the hop: the
    // destination the form picked, per operation, never a session-wide anchor.
    const withdraw = useCallback(
        async (
            specificAmount: bigint | undefined,
            destination: AnchorChainId,
            // the payout token; absent means the anchor's default stable
            destinationCurrency?: string,
        ) => {
            if (lockRef.current) return;

            if (!authedApi || !address) {
                setState((prev) => ({
                    ...prev,
                    status: 'failed',
                    error: 'Connect & sign in first',
                }));
                return;
            }

            // Resolve the amount: explicit, or the full *withdrawable* balance —
            // the saga can only move what the backend reports as withdrawable.
            const withdrawAmount =
                specificAmount ??
                (numericWithdrawable !== null && numericWithdrawable > 0
                    ? parseUnits(numericWithdrawable.toString(), USDC_DECIMALS)
                    : 0n);

            if (withdrawAmount <= 0n) {
                logStep('No balance to withdraw');
                toast.info('No funds available to withdraw.', {
                    duration: TOAST_DURATION,
                });
                setState(INITIAL_STATE);
                return;
            }

            lockRef.current = true;
            try {
                registeredBridgeIntentIds.current.clear();
                setState({
                    status: 'submitting',
                    amount: withdrawAmount,
                    error: null,
                    transferId: null,
                    jobId: null,
                    txHash: null,
                    progress: 5,
                    progressMessage: 'Submitting withdrawal job',
                });
                logStep('Starting withdraw', {
                    amount: withdrawAmount.toString(),
                    destination: address,
                });

                // RustSigner requires a fresh per-operation user signature
                // before it signs the withdraw's fund movement, and the bridge
                // hop additionally has to be registered on chain by the owner's
                // own wallet. The first attempt carries an empty
                // `fundAuthorizations` map (strict handshake opt-in); the
                // descriptors the backend answers with are verified against
                // this exact amount/owner, the bridge intent that comes with
                // them is registered on the anchor chain, the proof is signed,
                // and the retry carries both.
                const start = await runWithFundAuthorization(
                    async (fundAuthorizations, extras) =>
                        startWithdraw(authedApi, {
                            amount: withdrawAmount.toString(),
                            anchorChainId: destination,
                            destinationChainId: destination,
                            destinationCurrency:
                                destinationCurrency ??
                                ANCHOR_CHAINS[destination].stable.address,
                            destinationType: 'eoa',
                            ...bridgeRequestField(),
                            fundAuthorizations,
                            ...(extras.bridgeIntent
                                ? { bridgeIntent: extras.bridgeIntent }
                                : {}),
                        }),
                    {
                        connectedAddress: address,
                        amount: withdrawAmount,
                        flow: 'withdrawal',
                        anchorChainId: destination,
                        resolveHyperUsdcAddress: resolveHyperUsdc,
                        // The quote lands with the challenge, BEFORE the wallet
                        // prompt opens, so the form's own fee line and its
                        // "You'll receive" figure are the real ones while the
                        // user still has the choice to cancel. Same sticky
                        // merge as the poll loop: a later round that omits a
                        // field must not blank what is already displayed.
                        onFees: (fees) => {
                            const quoted = normalizeTransferFees(fees);
                            setState((prev) => ({
                                ...prev,
                                bridge: quoted.bridge ?? prev.bridge ?? null,
                                bridgeFeeUsd:
                                    quoted.bridgeFeeUsd ??
                                    prev.bridgeFeeUsd ??
                                    null,
                            }));
                        },
                        onPrompt: (method) =>
                            setState((prev) => ({
                                ...prev,
                                progressMessage:
                                    method === 'passkey'
                                        ? 'Confirm the withdrawal with your passkey'
                                        : method === 'transaction'
                                          ? 'Confirm the withdrawal in your wallet'
                                          : 'Sign the withdrawal in your wallet',
                            })),
                    },
                );

                setState((prev) => ({
                    ...prev,
                    status: statusFromJob(start),
                    jobId: start.jobId,
                    transferId: transferIdFromJob(start),
                    progress: start.progress,
                    progressMessage: start.message,
                    progressStep: start.step,
                    txHash: txHashFromJob(start) ?? prev.txHash,
                }));
                // client-side failures from here on belong to this job's diagnostic trail
                setActiveJobForErrorReports(start.jobId, factionsApi);
                logStep('Withdraw started', {
                    jobId: start.jobId,
                    transferId: transferIdFromJob(start),
                    status: start.status,
                });

                await trackWithdrawJob(start, withdrawAmount, destination);
            } catch (error) {
                // a signature rejection, a dropped request, a wallet error — none of these reach
                // the backend, so without this the job just looks frozen for no visible reason.
                reportJobException(error, { where: 'withdraw' });
                failWithdraw(error);
            } finally {
                // stop attributing later, unrelated failures to a finished operation
                setActiveJobForErrorReports(null, null);
                lockRef.current = false;
            }
        },
        [
            address,
            authedApi,
            factionsApi,
            failWithdraw,
            numericWithdrawable,
            resolveHyperUsdc,
            trackWithdrawJob,
        ],
    );

    const withdrawAll = useCallback(
        async (
            destinationChainId: AnchorChainId,
            destinationCurrency?: string,
        ) => {
            await withdraw(undefined, destinationChainId, destinationCurrency);
        },
        [withdraw],
    );

    /**
     * Backstop for a job that reaches a terminal state while nothing here is polling it.
     *
     * `trackWithdrawJob` is the normal path and reacts within one poll, but it only runs in
     * the tab that started or attached to the job and it gives up at its own deadline, and
     * the container only ever attaches ACTIVE jobs. A withdrawal that HyperCore refuses
     * (`coreSweepRejected`) therefore left the panel sitting on "Moving funds to the bridge
     * wallet" long after the job itself had failed, with no way for the user to learn that
     * their money was back in their wallet and the withdrawal was over.
     *
     * Only ever moves a tracked job forward: it ignores other jobs, and it never overwrites
     * a state that already settled.
     */
    const syncFromJob = useCallback(
        (job: SpeedJobResponse) => {
            const current = stateRef.current;
            if (job.kind !== 'withdraw' || job.jobId !== current.jobId) return;
            if (current.status === 'success' || current.status === 'failed') {
                return;
            }
            if (job.status === 'settled') {
                setState((prev) => ({
                    ...prev,
                    status: 'success',
                    txHash: txHashFromJob(job) ?? prev.txHash,
                    progress: 100,
                    progressMessage: 'Withdrawal confirmed',
                }));
                toast.success('Funds withdrawn successfully.', {
                    duration: TOAST_DURATION,
                });
                onSuccess?.();
                return;
            }
            if (job.status === 'failed' || job.status === 'refunded') {
                failWithdraw(failureFromJob(job));
            }
        },
        [failWithdraw, onSuccess],
    );

    const attach = useCallback(
        async (job: SpeedJobResponse) => {
            if (lockRef.current) return;
            if (!factionsApi || job.kind !== 'withdraw') {
                return;
            }

            lockRef.current = true;
            try {
                setState((prev) => ({
                    ...prev,
                    status: statusFromJob(job),
                    amount: amountFromJob(job) ?? prev.amount,
                    error: null,
                    jobId: job.jobId,
                    transferId: transferIdFromJob(job),
                    progress: job.progress,
                    progressMessage: job.message,
                    progressStep: job.step,
                    txHash: txHashFromJob(job) ?? prev.txHash,
                }));
                const latestJob = await fetchSpeedJob(factionsApi, job.jobId);
                await trackWithdrawJob(
                    latestJob,
                    amountFromJob(latestJob) ?? amountFromJob(job),
                    null,
                );
            } catch (error) {
                failWithdraw(error);
            } finally {
                lockRef.current = false;
            }
        },
        [factionsApi, failWithdraw, trackWithdrawJob],
    );

    const quoteWithdrawal = useCallback(
        async (
            amount: bigint,
            destinationChainId: AnchorChainId,
            signal: AbortSignal,
            destinationCurrency?: string,
        ) => {
            if (!authedApi) {
                throw new Error('Connect & sign in first');
            }
            return fetchWithdrawQuote(
                authedApi,
                {
                    amount: amount.toString(),
                    destinationChainId,
                    ...(destinationCurrency ? { destinationCurrency } : {}),
                    ...bridgeRequestField(),
                },
                signal,
            );
        },
        [authedApi],
    );

    return {
        state,
        status: state.status,
        withdraw,
        withdrawAll,
        quoteWithdrawal,
        reset,
        attach,
        syncFromJob,
        isWithdrawing:
            state.status === 'submitting' || state.status === 'bridging',
    };
}
